import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import SignInPage from '@/pages/SignInPage'
import { AppLayout } from './AppLayout'
import { RequireAdmin, RequireAuth } from './guards'
import { DetailSkeleton } from '@/components/states'

/**
 * Only the sign-in screen is eager — it is the first paint for a signed-out
 * visitor. Every authenticated route, and the whole admin surface with its
 * charts, is code-split so the initial download stays inside the §15.5 budget.
 */
const HomePage = lazy(() => import('@/pages/HomePage'))
const ForbiddenPage = lazy(() => import('@/pages/ForbiddenPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))
const DisciplinesPage = lazy(() => import('@/pages/DisciplinesPage'))
const DisciplineDetailPage = lazy(() => import('@/pages/DisciplineDetailPage'))
const CatalogPage = lazy(() => import('@/pages/CatalogPage'))
const BookDetailPage = lazy(() => import('@/pages/BookDetailPage'))
const MyLibraryPage = lazy(() => import('@/pages/MyLibraryPage'))
const AboutPage = lazy(() => import('@/pages/AboutPage'))

const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'))
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'))
const AdminBooksPage = lazy(() => import('@/pages/admin/AdminBooksPage'))
const AdminBookFormPage = lazy(() => import('@/pages/admin/AdminBookFormPage'))
const AdminDisciplinesPage = lazy(() => import('@/pages/admin/AdminDisciplinesPage'))
const AdminDisciplineLinkPage = lazy(() => import('@/pages/admin/AdminDisciplineLinkPage'))
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'))
const AdminModerationPage = lazy(() => import('@/pages/admin/AdminModerationPage'))
const AdminStatsPage = lazy(() => import('@/pages/admin/AdminStatsPage'))
const AdminAuditPage = lazy(() => import('@/pages/admin/AdminAuditPage'))

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<DetailSkeleton />}>{children}</Suspense>
}

export const router = createBrowserRouter([
  { path: '/signin', element: <SignInPage /> },
  { path: '/403', element: <Lazy><ForbiddenPage /></Lazy> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Lazy><HomePage /></Lazy> },
      { path: 'disciplines', element: <Lazy><DisciplinesPage /></Lazy> },
      { path: 'disciplines/:subjectId', element: <Lazy><DisciplineDetailPage /></Lazy> },
      { path: 'catalog', element: <Lazy><CatalogPage /></Lazy> },
      { path: 'books/:bookId', element: <Lazy><BookDetailPage /></Lazy> },
      { path: 'me', element: <Lazy><MyLibraryPage /></Lazy> },
      { path: 'about', element: <Lazy><AboutPage /></Lazy> },
      {
        path: 'admin',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminLayout />
            </Lazy>
          </RequireAdmin>
        ),
        children: [
          { index: true, element: <Lazy><AdminDashboardPage /></Lazy> },
          { path: 'books', element: <Lazy><AdminBooksPage /></Lazy> },
          { path: 'books/new', element: <Lazy><AdminBookFormPage /></Lazy> },
          { path: 'books/:bookId', element: <Lazy><AdminBookFormPage /></Lazy> },
          { path: 'disciplines', element: <Lazy><AdminDisciplinesPage /></Lazy> },
          { path: 'disciplines/:subjectId', element: <Lazy><AdminDisciplineLinkPage /></Lazy> },
          { path: 'users', element: <Lazy><AdminUsersPage /></Lazy> },
          { path: 'moderation', element: <Lazy><AdminModerationPage /></Lazy> },
          { path: 'stats', element: <Lazy><AdminStatsPage /></Lazy> },
          { path: 'audit', element: <Lazy><AdminAuditPage /></Lazy> },
        ],
      },
      { path: '404', element: <Lazy><NotFoundPage /></Lazy> },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
])
